import type { ContentProposal } from '@shared/contentProposal'

import { API_BASE_URL, apiService } from './api'

async function request<T>(bookId: string, suffix = '', body?: unknown): Promise<T> {
  const send = () =>
    fetch(`${API_BASE_URL}/api/content-proposals/${bookId}${suffix}`, {
      method: body ? 'PATCH' : 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
  let response = await send()
  if (response.status === 401) {
    const refreshed = await apiService.refreshToken()
    if (refreshed.success) {
      localStorage.setItem('token', refreshed.data.token)
      response = await send()
    }
  }
  const json = await response.json()
  if (!response.ok || !json.success) {
    throw new Error(json.message || 'לא ניתן לטעון הצעות')
  }
  return json.data
}

export const listContentProposals = (bookId: string, conversationId?: string) =>
  request<ContentProposal[]>(bookId, conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '')
export const CONTENT_PROPOSALS_CHANGED = 'content-proposals-changed'
export const updateContentProposal = async (
  bookId: string,
  item: ContentProposal,
  action: 'edit' | 'accept' | 'reject',
  markdown?: string,
  changeId?: string,
) => {
  try {
    return await request<ContentProposal>(bookId, `/${item.id}`, {
      action,
      version: item.version,
      ...(changeId !== undefined ? { changeId } : {}),
      ...(markdown !== undefined ? { markdown } : {}),
    })
  } finally {
    window.dispatchEvent(new Event(CONTENT_PROPOSALS_CHANGED))
  }
}

export interface ProposalBatchResult {
  completed: number
  failures: Array<{ id: string; message: string }>
}

/** Resolve the exact displayed versions, retaining conflicts for individual review. */
export async function decideContentProposals(
  bookId: string,
  items: ContentProposal[],
  action: 'accept' | 'reject',
): Promise<ProposalBatchResult> {
  const result: ProposalBatchResult = { completed: 0, failures: [] }
  try {
    for (const item of items) {
      try {
        if (action === 'accept' && !item.canApply) {
          throw new Error('ההצעה הוכנה בסביבת תוכן אחרת.')
        }
        await request<ContentProposal>(bookId, `/${item.id}`, { action, version: item.version })
        result.completed++
      } catch (error) {
        result.failures.push({ id: item.id, message: error instanceof Error ? error.message : 'עדכון ההצעה נכשל' })
      }
    }
    return result
  } finally {
    window.dispatchEvent(new Event(CONTENT_PROPOSALS_CHANGED))
  }
}
